/**
 * StockVideoFilters
 *
 * Responsive filter and sort controls for stock videos with premium gating
 * and dropdown portals.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Star, ChevronDown, Clock, Shuffle, X } from 'lucide-react';
import { STOCK_VIDEO_CATEGORY_LIST } from '../../../data/tags/tagEnums';
import { useToast } from '../../ui/notifications/toast-notification.jsx';

const VISIBLE_TAGS_COUNT = 8; // Default number of visible tags
const MAX_ACTIVE_FILTERS = 4; // Define the limit

function StockVideoFilters({
  activeFilter,
  setActiveFilter,
  sortMethod,
  setSortMethod,
  showSavedStockVideos,
  setShowSavedStockVideos,
  stockVideoCount,
  searchLoading,
  isLoggedIn = true,
  isBasicUser = false,
  isPremiumUser = true
}) {
  const { addToast } = useToast?.() || { addToast: () => {} };
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
  const [showCategoriesButton, setShowCategoriesButton] = useState(false);

  // --- State ---
  const [maxVisibleTags, setMaxVisibleTags] = useState(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 640) return 0;
    return VISIBLE_TAGS_COUNT;
  });
  
  const [activeFilters, setActiveFilters] = useState(() => {
    const initialActive = Array.isArray(activeFilter) ? activeFilter : (activeFilter ? [activeFilter] : []);
    return initialActive.filter(tag => STOCK_VIDEO_CATEGORY_LIST.includes(tag));
  });
  
  const [visibleTags, setVisibleTags] = useState([]);
  const [hiddenTags, setHiddenTags] = useState([]);

  // Refs
  const categoriesMenuRef = useRef(null);
  const moreMenuRef = useRef(null);
  const dropdownRef = useRef(null);
  const sortMenuRef = useRef(null);
  const sortDropdownRef = useRef(null);
  const filtersContainerRef = useRef(null);
  const tagsContainerRef = useRef(null);
  const controlsRef = useRef(null);

  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [sortDropdownPosition, setSortDropdownPosition] = useState({ top: 0, left: 0 });

  // Convert single filter to array for consistency
  useEffect(() => {
    const newActiveFilters = Array.isArray(activeFilter) ? activeFilter : (activeFilter ? [activeFilter] : []);
    const filteredActive = newActiveFilters.filter(tag => STOCK_VIDEO_CATEGORY_LIST.includes(tag));
    
    // Only update if the content actually changed
    if (JSON.stringify(filteredActive) !== JSON.stringify(activeFilters)) {
      setActiveFilters(filteredActive);
    }
  }, [activeFilter]);

  // Propagate activeFilters changes upwards - supporting multiple filters
  useEffect(() => {
    const newFilterValue = activeFilters.length > 0 ? activeFilters : null;
    
    // Only call setActiveFilter if the value actually changed (avoid infinite loops)
    const currentFilterString = JSON.stringify(Array.isArray(activeFilter) ? activeFilter : (activeFilter ? [activeFilter] : []));
    const newFilterString = JSON.stringify(activeFilters);
    
    if (currentFilterString !== newFilterString) {
      setActiveFilter(newFilterValue);
    }
  }, [activeFilters, activeFilter]);

  // Update dropdown positions on scroll and window resize
  useEffect(() => {
    const updateDropdownPositions = () => {
      if (isMoreMenuOpen) {
        const buttonRef = window.innerWidth < 640 
          ? categoriesMenuRef.current 
          : moreMenuRef.current;
          
        if (buttonRef) {
          const rect = buttonRef.getBoundingClientRect();
          setDropdownPosition({
            top: rect.bottom + window.scrollY,
            left: Math.min(rect.left + window.scrollX, window.innerWidth - 224)
          });
        }
      }
      if (isSortMenuOpen && sortMenuRef.current) {
        const rect = sortMenuRef.current.getBoundingClientRect();
        setSortDropdownPosition({
          top: rect.bottom + window.scrollY,
          left: Math.min(rect.left + window.scrollX, window.innerWidth - 160)
        });
      }
    };
    
    if (isMoreMenuOpen || isSortMenuOpen) {
      window.addEventListener('scroll', updateDropdownPositions);
      window.addEventListener('resize', updateDropdownPositions);
      updateDropdownPositions();
    }
    
    return () => {
      window.removeEventListener('scroll', updateDropdownPositions);
      window.removeEventListener('resize', updateDropdownPositions);
    };
  }, [isMoreMenuOpen, isSortMenuOpen]);

  // Central function to calculate and set visible/hidden tags
  const balanceTags = useCallback((currentMaxVisible, currentActiveFilters) => {
    const allTags = [...STOCK_VIDEO_CATEGORY_LIST];
    
    // First, prioritize active filters
    const activeTagsInOrder = allTags.filter(tag => currentActiveFilters.includes(tag));
    const inactiveTagsInOrder = allTags.filter(tag => !currentActiveFilters.includes(tag));
    
    // Combine with active tags first, then inactive tags
    const orderedTags = [...activeTagsInOrder, ...inactiveTagsInOrder];
    
    // Split into visible and hidden tags
    const newVisible = orderedTags.slice(0, Math.max(1, currentMaxVisible));
    const newHidden = orderedTags.slice(Math.max(1, currentMaxVisible));
    
    // Handle zero visible tags case (mobile view)
    if (currentMaxVisible <= 0) {
      setVisibleTags([]);
      setHiddenTags(orderedTags);
    } else {
      setVisibleTags(newVisible);
      setHiddenTags(newHidden);
    }
  }, []);

  // Handle responsive tag display calculation
  const handleResize = useCallback(() => {
    if (typeof window === 'undefined' || !filtersContainerRef.current || !tagsContainerRef.current || !controlsRef.current) return;

    const containerWidth = filtersContainerRef.current.clientWidth;
    const isMobileView = containerWidth < 640;
    const isIntermediateView = containerWidth >= 640 && containerWidth < 800;
    let calculatedMaxVisible;

    setShowCategoriesButton(isIntermediateView || isMobileView);

    if (isMobileView) {
      calculatedMaxVisible = 0;
    } else if (isIntermediateView) {
      calculatedMaxVisible = 1;
    } else {
      // Desktop View Calculation
      const controlsWidth = controlsRef.current.clientWidth + 20;
      const availableWidth = containerWidth - controlsWidth;
      const averageTagWidth = 120;

      let preliminaryMax = Math.max(1, Math.floor(availableWidth / averageTagWidth));
      preliminaryMax = Math.min(preliminaryMax, STOCK_VIDEO_CATEGORY_LIST.length);

      const needsMoreButton = STOCK_VIDEO_CATEGORY_LIST.length > preliminaryMax;
      const moreButtonWidth = needsMoreButton ? 100 : 0;

      calculatedMaxVisible = Math.max(1, Math.floor((availableWidth - moreButtonWidth) / averageTagWidth));
      calculatedMaxVisible = Math.min(calculatedMaxVisible, STOCK_VIDEO_CATEGORY_LIST.length);
    }

    if (calculatedMaxVisible !== maxVisibleTags) {
      setMaxVisibleTags(calculatedMaxVisible);
    }
  }, [maxVisibleTags]);

  // Set up resize observer and initial calculation
  useEffect(() => {
    balanceTags(maxVisibleTags, activeFilters);
  }, [maxVisibleTags, activeFilters, balanceTags]);

  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          !categoriesMenuRef.current?.contains(event.target) && !moreMenuRef.current?.contains(event.target)) {
        setIsMoreMenuOpen(false);
      }
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target) &&
          !sortMenuRef.current?.contains(event.target)) {
        setIsSortMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const isFilterLimitReached = activeFilters.length >= MAX_ACTIVE_FILTERS;

  const activateTag = (tag) => {
    if (!isPremiumUser) return; // Only allow premium users
    
    if (activeFilters.length >= MAX_ACTIVE_FILTERS) {
      addToast(`You can only select up to ${MAX_ACTIVE_FILTERS} tags at once. Remove a tag to add a new one.`, 'warning');
      return; 
    }
    // Add the tag to the front of the array, removing any existing instance
    setActiveFilters(prevActive => [tag, ...prevActive.filter(t => t !== tag)]);
    setIsMoreMenuOpen(false);
  };

  const deactivateTag = (tag) => {
    if (!isPremiumUser) return; // Only allow premium users
    setActiveFilters(activeFilters.filter(activeTag => activeTag !== tag));
  };

  const clearAllFilters = () => {
    if (!isPremiumUser) return; // Only allow premium users
    setActiveFilters([]);
  };

  const handleToggleTag = (tag) => {
    if (!isPremiumUser) return; // Only allow premium users
    
    if (activeFilters.includes(tag)) {
      deactivateTag(tag);
    } else {
      activateTag(tag);
    }
  };

  const handleMoreTagSelect = (tag) => {
    if (!isPremiumUser) return;
    
    if (activeFilters.includes(tag)) {
      deactivateTag(tag);
    } else {
      activateTag(tag);
    }
    setIsMoreMenuOpen(false);
  };

  const handleMoreClick = () => {
    if (!isPremiumUser) return;
    setIsMoreMenuOpen(prev => !prev);
  };

  const handleSortClick = (method) => {
    if (!isPremiumUser) return;
    setSortMethod(method);
    setIsSortMenuOpen(false);
  };

  const getSortIcon = (method) => {
    switch (method) {
      case 'newest':
        return <Clock className="w-4 h-4" />;
      case 'random':
      default:
        return <Shuffle className="w-4 h-4" />;
    }
  };

  const getSortLabel = (method) => {
    switch (method) {
      case 'newest':
        return 'Newest';
      case 'random':
      default:
        return 'Random';
    }
  };

  return (
    <div className="mb-4" ref={filtersContainerRef}>
      <div className="flex items-center justify-between mb-2">
        {/* Left side - Tags / Categories */}
        <div
          ref={tagsContainerRef}
          className="flex-1 overflow-x-auto pb-1 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-700 -mx-1 px-1"
        >
          <div className="flex space-x-2 min-w-min">
            {/* Categories Button - visible only when no visible tags AND on mobile/intermediate viewport sizes */}
            {visibleTags.length === 0 && (
              <div 
                ref={categoriesMenuRef} 
                className={showCategoriesButton ? "block sm:block relative" : "block sm:hidden relative"}
              >
                <button
                  onClick={handleMoreClick}
                  disabled={!isPremiumUser}
                  className={`px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium flex-shrink-0 flex items-center gap-1
                  ${!isPremiumUser
                    ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  } dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 h-10`}
                >
                  Categories <ChevronDown className={`h-4 w-4 transition-transform ${isMoreMenuOpen ? 'rotate-180' : ''}`} />
                </button>
              </div>
            )}

            {/* Desktop Tag Filters - Renders when there are visible tags OR when desktop with no categories button */}
            <div className={visibleTags.length > 0 || maxVisibleTags > 0 ? "hidden sm:flex space-x-2" : "hidden space-x-2"}>
              {visibleTags.map((tag) => {
                const isActive = activeFilters.includes(tag);
                const isDisabled = (!isPremiumUser) || (!isActive && isFilterLimitReached);
                return (
                  <button
                    key={tag}
                    onClick={() => handleToggleTag(tag)}
                    disabled={isDisabled}
                    className={`
                      px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium flex-shrink-0
                      transition-colors duration-150 ease-in-out my-1
                      ${isActive && isPremiumUser
                        ? 'bg-purple-100 text-purple-800 hover:bg-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:hover:bg-purple-900/50 ring-1 ring-purple-300 dark:ring-purple-700'
                        : isPremiumUser
                          ? 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                          : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-500'}
                      ${isDisabled ? 'opacity-60 cursor-not-allowed' : ''}
                    `}
                  >
                    {tag}
                  </button>
                )
              })}

              {/* More dropdown button - Show whenever there are hidden tags */}
              {hiddenTags.length > 0 && (
                <div 
                  ref={moreMenuRef} 
                  className="relative my-1 inline-block"
                >
                  <button
                    onClick={handleMoreClick}
                    disabled={!isPremiumUser}
                    className={`px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium flex-shrink-0 flex items-center gap-1
                      ${!isPremiumUser
                        ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      } dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700`}
                  >
                    +{hiddenTags.length} more <ChevronDown className={`h-4 w-4 transition-transform ${isMoreMenuOpen ? 'rotate-180' : ''}`} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Clear Filters button - show when multiple filters are active */}
        {activeFilters.length > 1 && (
          <div className="flex items-center justify-start sm:justify-center mt-3 mb-3 text-sm text-gray-600 dark:text-gray-400 px-1">
            <button 
              onClick={clearAllFilters}
              disabled={!isPremiumUser}
              className={`flex items-center gap-1 ${
                !isPremiumUser 
                  ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-60' 
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              } transition-colors`}
            >
              <X className="h-4 w-4" />
              Clear Filters
            </button>
          </div>
        )}

        {/* Right side controls */}
        <div ref={controlsRef} className="flex items-center gap-2 pl-2 my-1">
          {/* Saved filter */}
          {isLoggedIn && (
            <button
              onClick={() => isPremiumUser && setShowSavedStockVideos(!showSavedStockVideos)}
              disabled={!isPremiumUser} // Only Premium users can save videos
              className={`
                flex items-center justify-center gap-1 px-3 sm:px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium border
                ${showSavedStockVideos && isPremiumUser
                  ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white border-purple-500 dark:border-purple-600'
                  : !isPremiumUser
                    ? 'bg-white text-gray-500 border-gray-200 opacity-60 cursor-not-allowed dark:bg-gray-900 dark:text-gray-500 dark:border-gray-800'
                    : 'bg-white text-gray-700 border-gray-200 hover:bg-purple-50 hover:text-purple-600 hover:border-purple-200 dark:bg-gray-900 dark:text-gray-300 dark:border-gray-800 dark:hover:bg-purple-900/20 dark:hover:text-purple-400 dark:hover:border-purple-800'}
              `}
            >
              <Star className={`h-4 w-4 ${showSavedStockVideos && isPremiumUser ? 'fill-white' : ''}`} />
              <span className="hidden xs:inline">Saved</span>
            </button>
          )}

          {/* Sort dropdown */}
          <div 
            ref={sortMenuRef} 
            className="flex items-center relative"
          >
            <button
              onClick={() => isPremiumUser && setIsSortMenuOpen(!isSortMenuOpen)}
              disabled={!isPremiumUser}
              className={`flex items-center justify-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium border 
                ${!isPremiumUser
                  ? 'bg-white text-gray-500 border-gray-200 opacity-60 cursor-not-allowed dark:bg-gray-900 dark:text-gray-500 dark:border-gray-800'
                  : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 dark:bg-gray-900 dark:text-gray-300 dark:border-gray-800 dark:hover:bg-gray-800'}`}
            >
              {getSortIcon(sortMethod)}
              <span className="hidden xs:inline">{getSortLabel(sortMethod)}</span>
              <ChevronDown className={`h-4 w-4 transition-transform ${isSortMenuOpen ? 'rotate-180' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Filter Count and Clear Button */}
      {activeFilters.length > 0 && (
        <div className="flex items-center justify-start sm:justify-center mt-3 mb-3 text-sm text-gray-600 dark:text-gray-400 px-1">
          <button 
            onClick={clearAllFilters}
            disabled={!isPremiumUser}
            className={`flex items-center gap-1 ${
              !isPremiumUser
                ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-60' 
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            } transition-colors`}
          >
            <X className="h-4 w-4" />
            Clear Filters
          </button>
        </div>
      )}

      {/* More/Categories Dropdown Portal */}
      {isMoreMenuOpen && isPremiumUser && (
        <div
          ref={dropdownRef}
          className="fixed z-50 mt-2 w-56 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-gray-800 max-h-[300px] overflow-y-auto"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            maxHeight: '300px'
          }}
        >
          <div className="py-2 px-2">
             {/* Show all categories when using Categories button (no visible tags), otherwise show hidden tags */}
             {(visibleTags.length === 0 ? STOCK_VIDEO_CATEGORY_LIST : hiddenTags)
              .map((tag) => {
                const isActive = activeFilters.includes(tag);
                const isDisabled = !isActive && isFilterLimitReached;
                return (
                  <button
                    key={tag}
                    onClick={() => handleMoreTagSelect(tag)} 
                    disabled={isDisabled}
                    className={`
                      w-full text-left px-3 py-2 rounded-md text-sm mb-1 flex justify-between items-center transition-colors
                      ${isActive 
                        ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                        : 'hover:bg-gray-100 text-gray-700 dark:text-gray-300 dark:hover:bg-gray-700'}
                      ${isDisabled ? 'opacity-60 cursor-not-allowed' : ''}
                    `}
                  >
                    <span>{tag}</span>
                    {isActive && (
                      <span className="ml-2 text-purple-600 dark:text-purple-400">✓</span>
                    )}
                  </button>
                );
              })}
          </div>
        </div>
      )}

      {/* Sort Dropdown Portal */}
      {isSortMenuOpen && isPremiumUser && (
        <div
          ref={sortDropdownRef}
          className="fixed z-50 mt-2 w-40 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-gray-800"
          style={{
            top: `${sortDropdownPosition.top}px`,
            left: `${sortDropdownPosition.left}px`,
          }}
        >
          <div className="py-2">
            {['random', 'newest'].map((method) => (
              <button
                key={method}
                onClick={() => handleSortClick(method)}
                className={`
                  w-full text-left px-3 py-2 text-sm transition-colors flex items-center
                  ${sortMethod === method
                    ? 'bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300'
                    : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'}
                `}
              >
                {getSortIcon(method)}
                <span className="ml-2">{getSortLabel(method)}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default StockVideoFilters; 